import { normalizeNumberInput } from "@/lib/parse-number";

/**
 * A typed number split into exact parts, so a 24-digit integer or a long decimal tail
 * survives untouched (a `number` would round both).
 */
export interface DecimalNumber {
  negative: boolean;
  /** Decimal digits without leading zeros ("0" for zero); a string so 24 digits stay exact. */
  integer: string;
  /** Digits after the decimal mark, exactly as typed ("" when there are none). */
  fraction: string;
}

/** Longest integer part the language converters read: 24 digits (below 10^24). */
export const MAX_INTEGER_DIGITS = 24;
/** Longest decimal tail read digit by digit. */
export const MAX_FRACTION_DIGITS = 20;

export type ReadError = "empty" | "invalid" | "tooBig" | "tooLong";

export type ReadResult =
  | { ok: true; value: DecimalNumber }
  | { ok: false; error: ReadError };

/**
 * Read what a person typed: grouping spaces, a lone or repeated separator, a minus sign
 * and a decimal tail are handled by the site's shared `normalizeNumberInput`; this adds
 * the exact big-integer split and the size limits.
 */
export function readDecimalNumber(raw: string): ReadResult {
  if (raw.trim() === "") {
    return { ok: false, error: "empty" };
  }
  const normalized = normalizeNumberInput(raw);
  if (normalized === null) {
    return { ok: false, error: "invalid" };
  }
  const negative = normalized.startsWith("-");
  const unsigned =
    negative || normalized.startsWith("+") ? normalized.slice(1) : normalized;
  const [intPart = "", fraction = ""] = unsigned.split(".");
  const digits = intPart.replace(LEADING_ZEROS, "");
  if (digits.length > MAX_INTEGER_DIGITS) {
    return { ok: false, error: "tooBig" };
  }
  if (fraction.length > MAX_FRACTION_DIGITS) {
    return { ok: false, error: "tooLong" };
  }
  const integer = digits === "" ? "0" : digits;
  const isZero = integer === "0" && !NON_ZERO.test(fraction);
  return {
    ok: true,
    value: { negative: negative && !isZero, integer, fraction },
  };
}

const LEADING_ZEROS = /^0+/;
const NON_ZERO = /[1-9]/;

/** A whole number as plain decimal digits: "0", or 1 to 24 digits with no leading zero. */
const WHOLE_DIGITS = /^(?:0|[1-9]\d{0,23})$/;

/** True for a string `splitGroups` and the spellers can read. */
export function isWholeDigits(digits: string): boolean {
  return WHOLE_DIGITS.test(digits);
}

/** Split decimal digits into base-10^`size` groups, least significant first ("0" gives none). */
export function splitGroups(digits: string, size: number): number[] {
  const groups: number[] = [];
  for (let end = digits.length; end > 0; end -= size) {
    const value = Number(digits.slice(Math.max(0, end - size), end));
    groups.push(value);
  }
  while (groups.length > 0 && groups.at(-1) === 0) {
    groups.pop();
  }
  return groups;
}
